import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";

const SEEN_KEY = "fms_seen_notif_ids";

function playChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, now);
    osc.frequency.exponentialRampToValueAtTime(1320, now + 0.14);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.12, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.35);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.4);
    setTimeout(() => { try { ctx.close(); } catch { /* noop */ } }, 500);
  } catch { /* silent — some browsers block until user gestures */ }
}

export function useNotifications(user) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const prevUnread = useRef(null);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/notifications");
      const list = data.items || [];
      const nextUnread = data.unread || 0;
      // Play subtle chime when unread count grows compared to last poll (not on first load)
      if (prevUnread.current !== null && nextUnread > prevUnread.current) playChime();
      prevUnread.current = nextUnread;
      setItems(list);
      setUnread(nextUnread);
      const seen = new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || "[]"));
      if (typeof Notification !== "undefined" && Notification.permission === "granted") {
        const fresh = list.filter((n) => !n.read && !seen.has(n.id));
        for (const n of fresh.slice(0, 3)) {
          try { new Notification(n.title, { body: n.body || "", tag: n.id }); } catch { /* noop */ }
          seen.add(n.id);
        }
        localStorage.setItem(SEEN_KEY, JSON.stringify([...seen].slice(-500)));
      }
    } catch { /* silent */ }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      setTimeout(() => Notification.requestPermission().catch(() => {}), 2000);
    }
    load();
    const t = setInterval(load, 20000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [user, load]);

  const markRead = async (id) => {
    try { await api.patch(`/notifications/${id}/read`); load(); } catch { /* noop */ }
  };
  const markAllRead = async () => {
    try { await api.post("/notifications/read-all"); load(); } catch { /* noop */ }
  };

  return { items, unread, load, markRead, markAllRead };
}
